import { useEffect } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { LogOut, User, ChevronLeft } from "lucide-react";
import { useReaderAuth } from "@/contexts/ReaderAuthContext";

export default function PublicLayout() {
    const { reader, logout } = useReaderAuth();
    const navigate = useNavigate();

    useEffect(() => { document.title = "Contenuti Extra · Prime Vision"; }, []);

    const onLogout = async () => {
        await logout();
        navigate("/extra");
    };

    return (
        <div className="pvx-public min-h-screen relative overflow-hidden text-pvx-textmain">
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br from-pvx-primary/[0.06] via-pvx-background to-pvx-azure/[0.10]" />
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 opacity-60" style={{ backgroundImage: "radial-gradient(circle at 12% 18%, rgb(var(--pvx-rgb-primary) / 0.12), transparent 55%), radial-gradient(circle at 88% 82%, rgb(var(--pvx-rgb-azure) / 0.12), transparent 55%)" }} />

            <header className="sticky top-0 z-30 pvx-glass border-b border-pvx-border/[0.08]">
                <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-3">
                    <Link
                        to="/"
                        className="flex items-center group"
                        data-testid="public-brand"
                        aria-label="Prime Vision — torna alla homepage"
                    >
                        <img
                            src="/assets/prime-vision-logo.png"
                            alt="Prime Vision"
                            data-testid="public-brand-logo"
                            className="h-[34px] md:h-[40px] w-auto object-contain transition-opacity group-hover:opacity-85"
                        />
                    </Link>

                    <div className="flex items-center gap-2">
                        {reader ? (
                            <>
                                <div className="hidden md:flex items-center gap-2 pr-2 border-r border-pvx-border/[0.08]">
                                    <div className="h-7 w-7 rounded-full bg-pvx-primary text-white text-[11px] font-medium flex items-center justify-center">
                                        {(reader.first_name || reader.email || "L")[0].toUpperCase()}
                                    </div>
                                    <div className="flex flex-col leading-tight text-right">
                                        <span className="text-[12.5px] font-medium">{reader.first_name} {reader.last_name}</span>
                                        <span className="text-[10.5px] text-pvx-textsubtle truncate">{reader.email}</span>
                                    </div>
                                </div>
                                <button
                                    onClick={onLogout}
                                    data-testid="reader-logout-btn"
                                    className="h-8 px-2.5 rounded-[10px] hover:bg-pvx-surface2 flex items-center gap-1.5 text-[12.5px] text-pvx-textmuted transition-colors"
                                >
                                    <LogOut size={13.5} strokeWidth={1.5} />
                                    Esci
                                </button>
                            </>
                        ) : (
                            <Link
                                to="/reader/login"
                                className="h-8 px-3 rounded-[10px] bg-pvx-primary text-white hover:opacity-90 flex items-center gap-1.5 text-[12.5px] font-medium transition-opacity"
                                data-testid="reader-header-login"
                            >
                                <User size={13.5} strokeWidth={1.5} />
                                Accedi
                            </Link>
                        )}
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-10 md:py-14">
                <Outlet />
            </main>

            <footer className="max-w-6xl mx-auto px-6 py-8 text-[11px] text-pvx-textsubtle">
                © Prime Vision — Contenuti Extra riservati ai lettori.
            </footer>
        </div>
    );
}

export function BackButton({ to = "/extra", label = "Torna agli extra" }) {
    return (
        <Link
            to={to}
            data-testid="back-to-library"
            className="inline-flex items-center gap-1.5 text-[13px] text-pvx-textmuted hover:text-pvx-textmain transition-colors mb-6"
        >
            <ChevronLeft size={15} strokeWidth={1.5} />
            {label}
        </Link>
    );
}
